import React, { useState, useEffect, useRef } from 'react';
import { formatDate } from '../utils/formatDate';
import { daysSince } from '../utils/localDate';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from './Icon';
import { STORAGE_KEYS } from '../constants/storageKeys';
import '../styles/components/AuthButton.css';

const formatLastLogin = (iso: string): string => {
  try {
    const date = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    // Calendar days in the viewer's timezone: 23:50 yesterday is "Yesterday".
    const diffDays = daysSince(date, now) ?? 0;

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(date);
  } catch {
    return '—';
  }
};

const AuthButton: React.FC = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [lastLogin, setLastLogin] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.LAST_LOGIN);
    if (stored) setLastLogin(stored);
  }, [isAuthenticated]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  if (isLoading) {
    return <div className="profile-trigger loading" />;
  }

  if (!isAuthenticated || !user) return null;

  const initials = user.name
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleSettings = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  return (
    <div className="profile-wrapper" ref={dropdownRef}>
      <button
        className={`profile-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(o => !o)}
        title={user.name}
        aria-label="User menu"
      >
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="profile-avatar" />
        ) : (
          <div className="profile-avatar-initials">{initials}</div>
        )}
        <span className="profile-chevron">
          <Icon name={isOpen ? 'chevronLeft' : 'chevronRight'} size={12} />
        </span>
      </button>

      {isOpen && (
        <div className="profile-dropdown">
          {/* User info block */}
          <div className="profile-dropdown-header">
            <div className="profile-dropdown-avatar">
              {user.picture ? (
                <img src={user.picture} alt={user.name} />
              ) : (
                <div className="profile-dropdown-initials">{initials}</div>
              )}
            </div>
            <div className="profile-dropdown-info">
              <span className="profile-dropdown-name">{user.name}</span>
              <span className="profile-dropdown-email">{user.email}</span>
            </div>
          </div>

          {lastLogin && (
            <div className="profile-dropdown-meta">
              <Icon name="clock" size={12} />
              Last login: {formatLastLogin(lastLogin)}
            </div>
          )}

          <div className="profile-dropdown-divider" />

          <button className="profile-dropdown-item" onClick={handleSettings}>
            <Icon name="settings" size={15} />
            Settings
          </button>

          <div className="profile-dropdown-divider" />

          <button className="profile-dropdown-item danger" onClick={handleLogout}>
            <Icon name="logout" size={15} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthButton;
